import { NgClass } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ShareServiceHelper } from '@freelancer/share-helper';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { ButtonComponent } from '../button/button.component';
import { HoverColor, IconColor, IconSize } from '../icon';
import { IconModule } from '../icon/icon.module';
import { Margin } from '../margin';
import { FontColor, TextAlign, TextSize } from '../text';
import type { ShareIcons } from './share-buttons.types';
import {
  LIST_OF_SOCIAL_MEDIA_INFO,
  ShareButtonSizes,
  ShareButtonSpacing,
} from './share-buttons.types';

interface ShareButton {
  readonly buttonClassName: string;
  readonly displayText: string;
  readonly iconClassName: string;
  readonly iconName: string;
  readonly labelText: string;
  readonly shareUrl: string;
}

type ShareUrlDictionary = { [key in ShareIcons]: string };

@Component({
  standalone: true,
  selector: 'fl-share-buttons',
  imports: [ButtonComponent, IconModule, NgClass],
  template: `
    <!--
    Had to purposely move away from fl-button inputs because
    it seems that the css on classes gets overruled by the css on
    inputs when this component is used in share modal webapp compat.
    Also, all classes here are prefixed with ShareButton- to avoid
    css conflicts with legacy css.
    -->
    @for (button of shareButtons; track trackByIconName($index, button)) {
      <div class="Share-container">
        <fl-button
          [class]="getMarginClassString()"
          [ngClass]="button.buttonClassName"
          [link]="button.shareUrl"
          [class.mb-xxsmall]="showText"
          [class.mb-none]="!showText"
          (click)="onClick()"
        >
          <fl-icon
            class="Share-icon"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [label]="button.labelText"
            [name]="button.iconName"
            [ngClass]="button.iconClassName"
            [size]="iconSize"
          />
        </fl-button>
        @if (showText) {
          <span [class]="shareTextClasses">
            {{ button.displayText }}
          </span>
        }
      </div>
    }
  `,
  styleUrls: ['./share-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareButtonsComponent implements OnChanges, OnInit {
  private shareServiceHelper = inject(ShareServiceHelper);

  HoverColor = HoverColor;
  IconColor = IconColor;

  @Input() set size(value: ShareButtonSizes) {
    this.iconSize = this.getIconSize(value);
  }

  @Input() set sizeTablet(value: ShareButtonSizes) {
    this.iconSizeTablet = this.getIconSize(value);
  }

  @Input() set sizeDesktop(value: ShareButtonSizes) {
    this.iconSizeDesktop = this.getIconSize(value);
  }

  @HostBinding('attr.data-spacing')
  @Input()
  shareButtonSpacing: ShareButtonSpacing = 'space-evenly';

  @Input() shareUrl: string;
  @Input() shareTitle?: string;
  @Input() shareText?: string;
  @Input() shareIcons: readonly ShareIcons[] = [
    'facebook',
    'twitter',
    'linkedin',
    'reddit',
    'telegram',
  ];

  @Input() textColor: FontColor = FontColor.FOREGROUND;

  @Input() showText = true;

  @Input() buttonRightMargin: Margin = 'none';
  @Input() buttonRightTabletMargin?: Margin;
  @Input() buttonRightDesktopMargin?: Margin;
  @Input() buttonRightDesktopLargeMargin?: Margin;

  @Input() excludeShareTracking = false;

  @Output() onButtonClick = new EventEmitter<void>();

  iconSize: IconSize = IconSize.MID;
  iconSizeDesktop: IconSize;
  iconSizeTablet: IconSize;
  shareButtons: readonly ShareButton[];

  shareTextClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'excludeShareTracking' in changes ||
      'shareIcons' in changes ||
      'shareText' in changes ||
      'shareTitle' in changes ||
      'shareUrl' in changes
    ) {
      const baseLink = this.shareServiceHelper.buildShareUrl(
        this.shareUrl,
        this.excludeShareTracking,
      );
      const shareUrls = this.createShareUrls(
        baseLink,
        this.shareTitle,
        this.shareText,
      );
      this.shareButtons = this.shareIcons.map(shareIcon => ({
        // Name must match the generated CSS class name
        buttonClassName: `ShareButton${LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].className}`,
        displayText: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].text,
        // Name must match the generated CSS class name
        iconClassName: `ShareIcon${LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].className}`,
        iconName: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].iconName,
        labelText: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].labelText,
        shareUrl: shareUrls[shareIcon],
      }));
    }

    if ('textColor' in changes) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.shareTextClasses = textVariants({
      color: this.textColor,
      size: TextSize.XXSMALL,
      align: TextAlign.CENTER,
    });
  }

  getIconSize(input: ShareButtonSizes): IconSize {
    switch (input) {
      case 'mid':
        return IconSize.MID;
      case 'large':
        return IconSize.LARGE;
      default:
        return IconSize.MID;
    }
  }

  getMarginClassString(): string {
    const classes = ['Share-button'];

    const breakpoints = [
      { prefix: '', value: this.buttonRightMargin },
      { prefix: 'tablet:', value: this.buttonRightTabletMargin },
      { prefix: 'desktop:', value: this.buttonRightDesktopMargin },
      { prefix: 'desktop_large:', value: this.buttonRightDesktopLargeMargin },
    ];

    for (const { prefix, value } of breakpoints) {
      if (value) classes.push(`${prefix}mr-${value}`);
    }

    return classes.join(' ');
  }

  private createShareUrls(
    baseUrl: string,
    shareTitle?: string,
    shareText?: string,
  ): ShareUrlDictionary {
    const encodedLink = encodeURIComponent(baseUrl);
    const encodedTitle = shareTitle
      ? encodeURIComponent(shareTitle)
      : undefined;
    const encondedText = shareText ? encodeURIComponent(shareText) : undefined;

    return {
      facebook: `http://www.facebook.com/share.php?u=${encodedLink}`,
      linkedin: `http://www.linkedin.com/shareArticle?mini=true&url=${encodedLink}&title=${encodedTitle}&source=https://www.freelancer.com`,
      reddit: `https://reddit.com/submit?url=${encodedLink}&title=${encodedTitle}`,
      telegram: `https://t.me/share/url?url=${encodedLink}&text=${encondedText}`,
      twitter: `https://twitter.com/intent/tweet?text=${encondedText}: ${encodedLink}`,
      // pinterest: `https://pinterest.com/pin/create/bookmarklet/?url=${encodedLink}`,
    };
  }

  onClick(): void {
    this.onButtonClick.emit();
  }

  trackByIconName(_: number, item: ShareButton): string {
    return item.iconName;
  }
}
